import { Launch } from '@mui/icons-material';
import {
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Tooltip,
    Box,
} from '@mui/material';
import {
    FormAutocomplete,
    FormInputGroup,
    FormSelect,
    FormText,
} from 'components/form';
import { useNavigate } from 'react-router-dom';
import { FormSelectOption } from 'components/form/types';
import { toTitleCase } from 'helpers';
import { FirebaseUserProtected } from 'types/User/firebaseTypes';
import styles from '../../DeviceConfig.module.scss';
import { Device, RawSuppliersList } from 'types';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { isDeviceGen5 } from 'helpers/device';

export interface DeviceConfigAdminProps {
    userProtected?: FirebaseUserProtected;
    device?: Device;
    suppliers?: RawSuppliersList;
}

/** Admin section of the Device Details > Config page. This section is only relevant for admin (supplierId 0) users. */
export const DeviceConfigAdmin = ({
    userProtected,
    device,
    suppliers,
}: DeviceConfigAdminProps) => {
    const navigate = useNavigate();
    const statuses = ['shipped', 'deployed', 'retired'];
    const [isLoading, setIsLoading] = useState(true);
    const [supplierOptions, setSupplierOptions] = useState<FormSelectOption[]>(
        []
    );
    const [editSupplier, setEditSupplier] = useState(false);

    const dialTypes = useMemo(() => {
        return {
            Disabled: 'unknown',
            'Vertical Tank Equation': '5_2772_10_81',
            'Horizontal Tank Equation': '5_2824_5_94',
            'Bulk Tank Equation': '4in_5_95',
        };
    }, []);

    const isGen5Device = isDeviceGen5(device?.item_number ?? '');

    useEffect(() => {
        setIsLoading(true);
        if (suppliers && Object.keys(suppliers).length > 0) {
            setSupplierOptions(
                Object.entries(suppliers).map((d) => {
                    // By default, use the supplier id for the name (used for autocomplete label).
                    let name = d[0];

                    // However, if there is a string in the name slot, prefer that value instead.
                    if (typeof d[1] === 'string') name = d[1];

                    return {
                        name,
                        value: d[0],
                    };
                })
            );
            setIsLoading(false);
        }
    }, [suppliers]);

    return (
        <div
            data-testid="DeviceConfigAdmin"
            className={styles.DeviceConfigAdmin}
        >
            <Card className={styles.card}>
                <CardHeader title="Admin" />
                <CardContent>
                    <FormInputGroup>
                        <FormSelect
                            field="status"
                            options={statuses.map((status: string) => ({
                                value: status,
                                name: toTitleCase(status),
                            }))}
                            disabled={!userProtected?.privs?.status_edit}
                        />
                    </FormInputGroup>
                    <FormInputGroup>
                        <FormText
                            field="equation_id"
                            label="Equation ID"
                            disabled
                        />
                    </FormInputGroup>
                    <FormInputGroup>
                        <FormSelect
                            field="dialType"
                            label="Equation ID Override"
                            options={Object.entries(dialTypes).map(
                                ([name, value]) => ({
                                    name,
                                    value,
                                })
                            )}
                            disabled={!isGen5Device}
                        />
                    </FormInputGroup>
                    {!device || isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <div className={styles.supplierContainer}>
                            {editSupplier ? (
                                <FormAutocomplete
                                    disableClearable
                                    label="Supplier"
                                    field="deviceSupplierId"
                                    options={supplierOptions}
                                    disabled={
                                        !userProtected?.privs?.supplier_edit
                                    }
                                    onBlur={() => setEditSupplier(false)}
                                    className={styles.supplierInput}
                                />
                            ) : (
                                <Box
                                    onClick={() => setEditSupplier(true)}
                                    sx={{ width: '100%' }}
                                >
                                    <FormText
                                        disabled={
                                            !userProtected?.privs?.supplier_edit
                                        }
                                        label="Supplier"
                                        field="deviceSupplierName"
                                        className={styles.supplierInput}
                                    />
                                </Box>
                            )}
                            <div className={styles.moreInfoButtonContainer}>
                                <Tooltip title="View current supplier">
                                    <IconButton
                                        onClick={() => {
                                            if (device?.protected?.supplierId) {
                                                navigate(
                                                    `/supplier-details/${device.protected.supplierId}/accounts`,
                                                    {
                                                        state: {
                                                            id: device.protected
                                                                .supplierId,
                                                        },
                                                    }
                                                );
                                            }
                                        }}
                                    >
                                        <Launch />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    )}

                    {device?.clone_of && (
                        <div>
                            <strong>
                                Cloned:{' '}
                                {device.clone_time
                                    ? format(
                                          device?.clone_time,
                                          'MM/dd/yy hh:mm a'
                                      )
                                    : null}
                            </strong>

                            <Tooltip title="View original device">
                                <IconButton
                                    onClick={() => {
                                        navigate(
                                            `/device-details/${device.clone_of}`
                                        );
                                    }}
                                >
                                    <Launch />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
